// Students Progress — step 2: ranked, paginated student list for one
// course ("/app/students-progress/:courseId"). Ranked by a blended
// Test + Daily Performance average computed server-side
// (GET /teacher/courses/:courseId/students-progress); this page just
// renders the page it's given plus Prev/Next controls. Clicking a row
// drills into StudentProgressDetail.jsx.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;

export default function StudentsProgressCourse() {
  const { user } = useAuth();
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (user?.role !== "teacher" && user?.role !== "admin") return;
    api.get(`/teacher/courses/${courseId}/students-progress`, { params: { page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)));
  }, [courseId, page, user]);

  if (user?.role !== "teacher" && user?.role !== "admin") {
    return <p className="text-sm text-red-600" data-testid="students-progress-course-forbidden">This page is for teachers only.</p>;
  }
  if (!data) return <p className="text-sm text-zinc-500" data-testid="students-progress-course-loading">Loading…</p>;

  const totalPages = Math.max(Math.ceil(data.total / data.page_size), 1);
  const pct = (v) => (v == null ? "—" : `${v}%`);

  return (
    <div className="space-y-6" data-testid="students-progress-course-page">
      <Link to="/app/students-progress" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950">
        <ArrowLeft className="w-4 h-4" /> Back to courses
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-700">Students Progress</p>
        <h1 className="font-heading text-3xl font-black tracking-tight mt-1" data-testid="students-progress-course-title">{data.course_title}</h1>
        <p className="text-sm text-zinc-500 mt-2">{data.total} enrolled student{data.total === 1 ? "" : "s"}, ranked by overall average.</p>
      </div>

      {data.items.length === 0 ? (
        <p className="text-sm text-zinc-500 py-8" data-testid="students-progress-course-empty">No students enrolled in this course yet.</p>
      ) : (
        <>
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs uppercase tracking-[0.1em] text-zinc-500">
                <tr>
                  <th className="px-5 py-3 font-semibold">Rank</th>
                  <th className="px-5 py-3 font-semibold">Student</th>
                  <th className="px-5 py-3 font-semibold">Student ID</th>
                  <th className="px-5 py-3 font-semibold">Overall Avg</th>
                  <th className="px-5 py-3 font-semibold">Daily Performance Avg</th>
                  <th className="px-5 py-3 font-semibold">Test Avg</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.student_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`students-progress-row-${s.student_id}`}>
                    <td className="px-5 py-3 font-bold">{s.rank ?? "—"}</td>
                    <td className="px-5 py-3">
                      <Link to={`/app/students-progress/${courseId}/${s.student_id}`} data-testid={`students-progress-view-${s.student_id}`} className="font-semibold text-blue-700 hover:underline">
                        {s.student_name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-zinc-500">{s.student_email}</td>
                    <td className="px-5 py-3 font-semibold">{pct(s.overall_avg)}</td>
                    <td className="px-5 py-3 text-zinc-500">{pct(s.dp_avg_pct)}</td>
                    <td className="px-5 py-3 text-zinc-500">{pct(s.test_avg_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1}
                data-testid="students-progress-prev-page"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <span className="text-sm text-zinc-500">Page {data.page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page >= totalPages}
                data-testid="students-progress-next-page"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
